// frontend/src/utils/errors.js
// ---------------------------------------------------------------------------
// Helper i18n pour traduire les codes d'erreur API en messages localisés.
//
// i18n Phase 6 — uniformisation des erreurs API.
//
// Les routes backend renvoient désormais des codes courts type :
//   { error: 'invalid_plan', details: { expected: ['decouverte', 'pro', 'intensif'] } }
// au lieu de strings FR codées en dur. Ce helper :
//   - lit le code dans response.error
//   - lit les params d'interpolation dans response.details
//   - retourne la string traduite via i18next (namespace 'errors')
//   - fallback sur `_unknown` si la clé n'existe pas dans le bundle
//
// Sécurité : pas de dangerouslySetInnerHTML, pas d'eval. Juste i18n.t().
// L'interpolation {{var}} de i18next est safe (escape par défaut).
// ---------------------------------------------------------------------------

import i18n from '../i18n';

/**
 * translateApiError({ error: 'invalid_plan', details: { expected: [...] } })
 *   → 'Formule invalide. Choix possibles : decouverte, pro, intensif.'
 *
 * Accepte aussi un simple string code :
 *   translateApiError('user_not_found') → 'Utilisateur introuvable'
 *
 * @param {object|string|null} errResponse - Body JSON de la réponse erreur,
 *   ou directement un code d'erreur en string, ou null/undefined.
 * @returns {string} Message localisé prêt à afficher.
 */
export function translateApiError(errResponse) {
  if (!errResponse) {
    return i18n.t('errors:_unknown');
  }

  // Forme 1 : { error: 'code', details: {...} } (cas standard backend)
  // Forme 2 : 'code' (raccourci côté caller)
  const code = typeof errResponse === 'string' ? errResponse : errResponse.error;
  if (!code || typeof code !== 'string') {
    return i18n.t('errors:_unknown');
  }

  // Normalisation des details pour interpolation i18next.
  // Cas spécial : si details.expected est un Array, on le join pour
  // qu'il s'affiche proprement dans {{expected}} sans afficher [object Object]
  // ou une virgule sans espace.
  const details = (typeof errResponse === 'object' && errResponse.details) || {};
  const interpolation = { ...details };
  if (Array.isArray(interpolation.expected)) {
    interpolation.expected = interpolation.expected.join(', ');
  }

  // i18next.t() retourne la clé brute si elle n'est pas trouvée dans le bundle
  // (ex: 'errors:weird_new_code'). On détecte ce cas avec defaultValue: null
  // pour fallback propre vers _unknown.
  const translated = i18n.t(`errors:${code}`, {
    ...interpolation,
    defaultValue: null,
  });

  if (translated === null || translated === undefined || translated === '') {
    // Log uniquement en dev pour aider à repérer les codes manquants.
    // En prod (Vercel), console.warn reste discret.
    if (process.env.NODE_ENV !== 'production') {
      console.warn('[translateApiError] code inconnu, fallback _unknown:', code);
    }
    return i18n.t('errors:_unknown');
  }

  return translated;
}

/**
 * Variante "safe fetch" : prend une Response fetch (déjà awaited),
 * tente de parser le body en JSON, et retourne le message traduit.
 *
 * Usage typique :
 *   const res = await fetch(...);
 *   if (!res.ok) {
 *     setError(await translateApiErrorResponse(res));
 *     return;
 *   }
 */
export async function translateApiErrorResponse(response) {
  if (!response) return i18n.t('errors:_unknown');
  try {
    const data = await response.json();
    return translateApiError(data);
  } catch {
    // Body non-JSON (ex: 504 gateway, HTML 502 Vercel) → fallback générique
    if (response.status >= 500) {
      return i18n.t('errors:server_error');
    }
    return i18n.t('errors:_unknown');
  }
}
